import { AssetClassCode, ExploreInsightDirection } from '@api';
import { Badge } from '@design/components/badge/Badge';
import { Icon } from '@design/components/icon/Icon';
import { formatPercentage } from '@helpers';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import clsx from 'clsx';
import { t } from 'i18next';

import styles from './InsightDetails.module.scss';

const SUFFIX_PERCENT = '%';
const DECIMALS_PERCENT = 2;
const SUFFIX_BPS = 'bps';
const DECIMALS_BPS = 0;

export interface InsightDetailsProps {
  horizon: string;
  stars: number;
  isNewInsight?: boolean;
  median: number;
  direction: ExploreInsightDirection;
  name: string;
  isTooltip?: boolean;
  entityTag?: string;
  episodeLabel: string;
}

export const InsightDetails = ({
  name,
  stars,
  horizon,
  median,
  direction,
  isNewInsight,
  isTooltip = false,
  entityTag,
  episodeLabel,
}: InsightDetailsProps) => {
  const { data } = useEntity(entityTag ?? '');

  const isFixedIncome = data?.data?.asset_class === AssetClassCode.Fi;
  const isBullish = direction === ExploreInsightDirection.Bullish;

  const suffix = isFixedIncome ? SUFFIX_BPS : SUFFIX_PERCENT;
  const decimals = isFixedIncome ? DECIMALS_BPS : DECIMALS_PERCENT;
  const formattedMedian = formatPercentage(median, {
    suffix,
    decimals,
  });

  return (
    <div
      className={clsx(styles.mainInfo, { [styles.isTooltip]: isTooltip })}
      data-insight-main-info
    >
      <div className={styles.insightNameWrapper}>
        <h4
          className={clsx(styles.insightName, {
            [styles.isTooltip]: isTooltip,
          })}
        >
          {name}
        </h4>
        {isNewInsight && (
          <Badge
            className={styles.badge}
            label={t('insights:insightCard.new')}
            data-testid="is-new-badge"
          />
        )}
      </div>
      <div className={styles.infoDetailWrapper}>
        <div
          className={clsx(styles.infoDetail, {
            [styles.isTooltip]: isTooltip,
          })}
        >
          <span
            className={clsx(styles.median, {
              [styles.isBullish]: isBullish,
              [styles.isBearish]: !isBullish,
            })}
          >
            {formattedMedian}
          </span>
          <span className={styles.dot}>&middot;</span>
          <span>{horizon.toUpperCase()}</span>
          <span className={styles.dot}>&middot;</span>
          <p
            className={styles.starsLabel}
            aria-label={`Rating: ${stars} stars`}
          >
            <Icon iconName="Rating" size={10} aria-hidden="true" />
            <span aria-current="true">{stars}</span>
          </p>
        </div>
        <span className={styles.episode}>{episodeLabel}</span>
      </div>
    </div>
  );
};
